// apps/web/src/pages/vendas/fechamento/confirmar-fechamento-dialog.tsx — Fase 12, Plano 14 (FECH-01, FECH-02, D-21, D-22, T-12-78).
// Diálogo 'Confirmar fechamento' + cartão de resultado. O botão fica desabilitado com spinner durante a requisição
// (anti-duplo-clique; o servidor ainda trava pela `versao` do PV); em erro o diálogo permanece aberto e a seleção é
// preservada. Sucesso: 'Retorno {ref} gerado' — o envio à Omie fica retido até a aprovação do rascunho.
import { useEffect, useState } from 'react';
import { CheckCircle2, FileClock, Loader2 } from 'lucide-react';
import type { FecharPvInput, ResultadoFechamento } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { FormMessage } from '../../../components/ui/form-message';
import { apiMutate } from '../../../lib/csrf-fetch';
import { chaveEstadoFiscal, corpoConfirmacao } from '../../../lib/vendas/fechamento';
import { ESTADO_FISCAL_CONFIG } from '../../pcp/retorno-panel';
import { AlertaConflitoPv } from '../detalhe/alerta-conflito-pv';
import { CODIGO_VERSAO_DESATUALIZADA, MENSAGEM_VERSAO_DESATUALIZADA } from '../use-pv-comandos';

export const MENSAGEM_FALHA_FECHAMENTO = 'Não foi possível registrar o fechamento. Nenhum Retorno foi gerado. Tente novamente.';

interface ConfirmarFechamentoDialogProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly pedidoId: string;
  /** Versão do PV vista pelo usuário (lock otimista). */
  readonly versao: number;
  readonly lotes: FecharPvInput['lotes'];
  /** Pendências do rascunho, em texto, vindas da simulação da API (D-22). */
  readonly pendencias: readonly string[];
  readonly onConfirmado: (resultado: ResultadoFechamento) => void;
  /** Recarrega o PV (conflito de versão); devolve true quando conseguiu. */
  readonly onRecarregar: () => Promise<boolean>;
}

export function ConfirmarFechamentoDialog({
  aberto,
  onAbertoChange,
  pedidoId,
  versao,
  lotes,
  pendencias,
  onConfirmado,
  onRecarregar,
}: ConfirmarFechamentoDialogProps) {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [conflito, setConflito] = useState<string | null>(null);
  const [recarregando, setRecarregando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setErro(null);
    setConflito(null);
  }, [aberto]);

  async function confirmar(): Promise<void> {
    if (enviando) return;
    setErro(null);
    setConflito(null);
    setEnviando(true);
    try {
      const corpo: FecharPvInput = { versao, lotes };
      const resposta = await apiMutate(`/api/v1/vendas/pedidos/${encodeURIComponent(pedidoId)}/fechamentos`, { method: 'POST', body: corpo });
      if (resposta.ok) {
        const resultado = (await resposta.json()) as ResultadoFechamento;
        onAbertoChange(false);
        onConfirmado(resultado);
        return;
      }
      let codigo: string | undefined;
      try {
        codigo = ((await resposta.json()) as { codigo?: string }).codigo;
      } catch {
        // Corpo vazio ou não-JSON: vale a cópia genérica.
      }
      if (resposta.status === 409 && codigo === CODIGO_VERSAO_DESATUALIZADA) setConflito(MENSAGEM_VERSAO_DESATUALIZADA);
      else setErro(MENSAGEM_FALHA_FECHAMENTO);
    } catch {
      setErro(MENSAGEM_FALHA_FECHAMENTO);
    } finally {
      setEnviando(false);
    }
  }

  async function recarregar(): Promise<void> {
    setRecarregando(true);
    try {
      if (await onRecarregar()) setConflito(null);
    } finally {
      setRecarregando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => (enviando ? undefined : onAbertoChange(proximo))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirmar fechamento</DialogTitle>
          <DialogDescription>{corpoConfirmacao({ lotes: lotes.length, pendencias })}</DialogDescription>
        </DialogHeader>
        {conflito && <AlertaConflitoPv mensagem={conflito} onRecarregar={() => void recarregar()} recarregando={recarregando} />}
        {erro && <FormMessage>{erro}</FormMessage>}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={enviando}>
            Voltar
          </Button>
          <Button type="button" onClick={() => void confirmar()} disabled={enviando || lotes.length === 0}>
            {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
            Confirmar fechamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ResultadoFechamentoCardProps {
  readonly resultado: ResultadoFechamento;
  /** Estado do faturamento Omie do Retorno (maiúsculo, como no banco); null enquanto o envio está retido. */
  readonly faturamentoEstado?: string | null;
  readonly onRevisar?: (retornoId: string) => void;
}

/** Cartão exibido depois de confirmar: Retorno gerado, estado do rascunho e do envio fiscal (retido até aprovar). */
export function ResultadoFechamentoCard({ resultado, faturamentoEstado = null, onRevisar }: ResultadoFechamentoCardProps) {
  const pendente = resultado.estadoRascunho === 'PENDENTE';
  const itensPendentes = new Set(resultado.pendencias.map((pendencia) => pendencia.itemRef ?? '__pedido')).size;
  const chave = chaveEstadoFiscal(faturamentoEstado);
  const estadoFiscal = chave ? ESTADO_FISCAL_CONFIG[chave] : null;
  return (
    <Card role="status" className="flex flex-col gap-2 px-5 py-4">
      <p className="flex items-center gap-2 text-sm font-semibold">
        <CheckCircle2 className="size-4 shrink-0 text-emerald-600" aria-hidden="true" />
        Fechamento registrado. Retorno {resultado.retornoRef} gerado.
      </p>
      {pendente && (
        <p className="flex items-center gap-2 text-sm text-amber-800">
          <FileClock className="size-4 shrink-0" aria-hidden="true" />
          Rascunho fiscal pendente: {itensPendentes} item(ns) a resolver.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">Envio fiscal:</span>
        {estadoFiscal ? (
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${estadoFiscal.corBadge}`}>{estadoFiscal.label}</span>
        ) : (
          <span className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
            Retido até a aprovação do rascunho
          </span>
        )}
      </div>
      {onRevisar && (
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => onRevisar(resultado.retornoId)}>
            Revisar rascunho
          </Button>
        </div>
      )}
    </Card>
  );
}
